import { apiClient } from './apiClient';
import type { ApiResponse } from '@/types/api.types';

export interface PatientInventoryTransaction {
  transactionId: number;
  itemId: number | null;
  itemName: string;
  categoryName: string | null;
  quantityDisbursed: number;
  quantityAdded: number;
  date: string | null;
  notes: string;
}

export interface PatientInventoryTransactionDetail extends PatientInventoryTransaction {
  categoryId: number | null;
  itemQuantityRemaining: number;
}

export interface DisbursableItem {
  itemId: number;
  categoryId: number | null;
  name: string;
  quantityRemaining: number;
}

/**
 * A `quote_item_costs_category` row. The ID matters: disbursable items name
 * their category by ID, so a title-only list cannot drive the Category filter.
 */
export interface InventoryCategory {
  quoteItemCostsCategoryID: number;
  title: string;
}

export interface CreateTransactionPayload {
  quoteItemCostId: number;
  quantityDisbursed: number;
  inventoryNotes?: string;
}

export function listInventoryTransactions(patientId: number): Promise<PatientInventoryTransaction[]> {
  return apiClient
    .get<ApiResponse<PatientInventoryTransaction[]>>(`/patients/${patientId}/inventory-transactions`)
    .then((r) => r.data.data);
}

export function getInventoryTransaction(
  patientId: number,
  transactionId: number,
): Promise<PatientInventoryTransactionDetail> {
  return apiClient
    .get<ApiResponse<PatientInventoryTransactionDetail>>(
      `/patients/${patientId}/inventory-transactions/${transactionId}`,
    )
    .then((r) => r.data.data);
}

export function createInventoryTransaction(
  patientId: number,
  payload: CreateTransactionPayload,
): Promise<{ transactionId: number; newRemaining: number }> {
  return apiClient
    .post<ApiResponse<{ transactionId: number; newRemaining: number }>>(
      `/patients/${patientId}/inventory-transactions`,
      payload,
    )
    .then((r) => r.data.data);
}

export function listDisbursableItems(): Promise<DisbursableItem[]> {
  return apiClient
    .get<ApiResponse<DisbursableItem[]>>('/inventory/items/disbursable')
    .then((r) => r.data.data);
}

/**
 * Categories for the disbursement modal's Category filter.
 *
 * Deliberately NOT `/inventory/categories`. That endpoint returns a bare
 * string[] of titles for the stock-list filter sidebar, which matches on
 * `c.title`; it cannot say which ID a title belongs to, and this modal filters
 * items by `categoryId`. Pointing at it left every option without a value and
 * rendered the dropdown empty. `/product-categories` serves the same
 * `quote_item_costs_category` table as full rows, scoped to the account and
 * already filtered to active=1 server-side.
 */
export function listInventoryCategories(): Promise<InventoryCategory[]> {
  return apiClient
    .get<ApiResponse<InventoryCategory[]>>('/product-categories')
    .then((r) => r.data.data ?? []);
}
